"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";

type Direction = "auto" | "to-roman" | "to-number";

const ROMAN_TABLE: { value: number; symbol: string }[] = [
  { value: 1000, symbol: "M" },
  { value: 900, symbol: "CM" },
  { value: 500, symbol: "D" },
  { value: 400, symbol: "CD" },
  { value: 100, symbol: "C" },
  { value: 90, symbol: "XC" },
  { value: 50, symbol: "L" },
  { value: 40, symbol: "XL" },
  { value: 10, symbol: "X" },
  { value: 9, symbol: "IX" },
  { value: 5, symbol: "V" },
  { value: 4, symbol: "IV" },
  { value: 1, symbol: "I" },
];

const ROMAN_CHAR_VALUES: Record<string, number> = {
  I: 1,
  V: 5,
  X: 10,
  L: 50,
  C: 100,
  D: 500,
  M: 1000,
};

const REFERENCE_VALUES = [1, 4, 5, 9, 10, 40, 50, 90, 100, 400, 500, 900, 1000];

const DIGITS_ONLY_REGEX = /^\d+$/;
const ROMAN_CHARS_REGEX = /^[IVXLCDMivxlcdm]+$/;

const DIRECTION_VALUES: Direction[] = ["auto", "to-roman", "to-number"];

/** English strings. A translated content file overrides these through `ui`. */
export const ROMAN_NUMERAL_CONVERTER_STRINGS = {
  directionLabels: {
    auto: "Auto-detect",
    "to-roman": "Number → Roman",
    "to-number": "Roman → Number",
  },
  direction: "Direction",
  numberOrRomanNumeral: "Number or Roman numeral",
  inputPlaceholder: "e.g. {year} or MMXIV",
  numberRangeError: "Enter a whole number between 1 and 3999.",
  invalidRoman:
    '"{input}" is not a valid Roman numeral (1-3999, standard notation).',
  autoDetectError:
    "Enter a number (1-3999) or a Roman numeral (I, V, X, L, C, D, M).",
  result: "Result",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  romanNumeralReference: "Roman Numeral Reference",
  number: "Number",
  romanNumeral: "Roman numeral",
  thisYear: "{year} (this year)",
} as const;

function toRoman(num: number): string {
  let remaining = num;
  let result = "";
  for (const { value, symbol } of ROMAN_TABLE) {
    while (remaining >= value) {
      result += symbol;
      remaining -= value;
    }
  }
  return result;
}

function parseRomanValue(roman: string): number {
  let total = 0;
  for (let i = 0; i < roman.length; i++) {
    const current = ROMAN_CHAR_VALUES[roman[i]];
    const next = i + 1 < roman.length ? ROMAN_CHAR_VALUES[roman[i + 1]] : 0;
    if (current < next) {
      total -= current;
    } else {
      total += current;
    }
  }
  return total;
}

function fromRoman(input: string): number | null {
  const roman = input.toUpperCase();
  if (!ROMAN_CHARS_REGEX.test(roman)) {
    return null;
  }
  const value = parseRomanValue(roman);
  if (value < 1 || value > 3999) {
    return null;
  }
  // Round-trip validation: re-encode and compare to reject sequences
  // like IIII, VX, or IC that are not standard Roman numerals.
  if (toRoman(value) !== roman) {
    return null;
  }
  return value;
}

type Strings = typeof ROMAN_NUMERAL_CONVERTER_STRINGS;

function convertNumber(
  trimmed: string,
  strings: Strings
): { result: string; error: string } {
  const num = parseIntegerInput(trimmed);
  if (num === null || num < 1 || num > 3999) {
    return { result: "", error: strings.numberRangeError };
  }
  return { result: toRoman(num), error: "" };
}

function convertRoman(
  trimmed: string,
  strings: Strings
): { result: string; error: string } {
  const value = fromRoman(trimmed);
  if (value === null) {
    return {
      result: "",
      error: t(strings.invalidRoman, { input: trimmed }),
    };
  }
  return { result: String(value), error: "" };
}

function convert(
  input: string,
  direction: Direction,
  strings: Strings
): {
  result: string;
  error: string;
} {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { result: "", error: "" };
  }
  if (direction === "to-roman") {
    return convertNumber(trimmed, strings);
  }
  if (direction === "to-number") {
    return convertRoman(trimmed, strings);
  }
  if (DIGITS_ONLY_REGEX.test(trimmed)) {
    return convertNumber(trimmed, strings);
  }
  if (ROMAN_CHARS_REGEX.test(trimmed)) {
    return convertRoman(trimmed, strings);
  }
  return {
    result: "",
    error: strings.autoDetectError,
  };
}

const CURRENT_YEAR = new Date().getFullYear();

export default function RomanNumeralConverterWidget() {
  const strings = useToolUi(ROMAN_NUMERAL_CONVERTER_STRINGS);
  const [input, setInput] = useState("");
  const [direction, setDirection] = useState<Direction>("auto");
  const [copied, setCopied] = useState(false);

  const { result, error } = convert(input, direction, strings);

  function handleCopy() {
    if (result.length === 0) {
      return;
    }
    writeToClipboard(result).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  return (
    <>
      {/* Direction */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.direction}
        </p>
        <div className="flex flex-wrap gap-3">
          {DIRECTION_VALUES.map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d}
            >
              <input
                checked={direction === d}
                className="accent-primary"
                name="direction"
                onChange={() => setDirection(d)}
                type="radio"
                value={d}
              />
              {strings.directionLabels[d]}
            </label>
          ))}
        </div>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="roman-input"
        >
          {strings.numberOrRomanNumeral}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 font-mono text-foreground text-lg placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="roman-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={t(strings.inputPlaceholder, { year: CURRENT_YEAR })}
          type="text"
          value={input}
        />
      </div>

      {/* Result */}
      {error.length > 0 && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
          {error}
        </p>
      )}
      {result.length > 0 && (
        <div className="space-y-3 rounded-lg border border-border bg-card p-6 text-center">
          <p className="text-muted-foreground text-sm">{strings.result}</p>
          <p className="break-all font-semibold text-4xl text-foreground tracking-wide md:text-5xl">
            {result}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={result.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Reference table */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.romanNumeralReference}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-border border-b bg-muted/30 text-left">
                <th className="p-3 font-medium text-foreground">
                  {strings.number}
                </th>
                <th className="p-3 font-medium text-foreground">
                  {strings.romanNumeral}
                </th>
              </tr>
            </thead>
            <tbody>
              {REFERENCE_VALUES.map((value) => (
                <tr
                  className="border-border border-b last:border-0"
                  key={value}
                >
                  <td className="p-3 text-muted-foreground">{value}</td>
                  <td className="p-3 font-mono text-foreground">
                    {toRoman(value)}
                  </td>
                </tr>
              ))}
              <tr className="bg-primary/5">
                <td className="p-3 text-foreground">
                  {t(strings.thisYear, { year: CURRENT_YEAR })}
                </td>
                <td className="p-3 font-mono font-semibold text-foreground">
                  {toRoman(CURRENT_YEAR)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
